(() => {
  const SELECTOR = '[data-polomi-lux-header]';

  function initialiseHeader(header) {
    if (header.dataset.initialised === 'true') return;
    header.dataset.initialised = 'true';

    const panel = header.querySelector('[data-polomi-lux-panel]');
    const hoverZone = header.querySelector('[data-polomi-lux-hover-zone]');
    const desktopLogo = header.querySelector('[data-polomi-lux-logo]');
    const mobileToggle = header.querySelector('[data-polomi-lux-mobile-toggle]');
    const cartToggle = header.querySelector('[data-cart-drawer-toggle]');
    const closeButtons = header.querySelectorAll('[data-polomi-lux-close]');
    const navigationView = header.querySelector('[data-polomi-navigation-view]');
    const cartView = header.querySelector('[data-polomi-cart-view]');
    const cartSlot = header.querySelector('[data-polomi-cart-slot]');
    const mobileQuery = window.matchMedia('(max-width: 749px)');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let closeTimer;
    let lastTrigger;
    let inertedElements = [];
    let hoverZoneFrame;

    const updateHomepageHoverZone = () => {
      if (!hoverZone || header.dataset.homepage !== 'true') return;
      const openingSection = document.querySelector('#MainContent > .shopify-section:first-child');
      const visibleBottom = openingSection
        ? Math.max(0, Math.min(window.innerHeight, openingSection.getBoundingClientRect().bottom))
        : 0;
      header.style.setProperty('--polomi-lux-home-hover-height', `${visibleBottom}px`);
    };

    const scheduleHomepageHoverZoneUpdate = () => {
      window.cancelAnimationFrame(hoverZoneFrame);
      hoverZoneFrame = window.requestAnimationFrame(updateHomepageHoverZone);
    };

    if (hoverZone && header.dataset.homepage === 'true') {
      updateHomepageHoverZone();
      window.addEventListener('scroll', scheduleHomepageHoverZoneUpdate, { passive: true });
      window.addEventListener('resize', scheduleHomepageHoverZoneUpdate);
    }

    const isOpen = () => ['initial', 'open'].includes(header.dataset.state);

    const registerCartModal = () => {
      const modals = window.Alpine?.store('modals');
      if (!modals) return null;
      modals.register('cart', 'rightDrawer');
      return modals;
    };

    if (!registerCartModal()) {
      document.addEventListener('alpine:initialized', registerCartModal, { once: true });
    }

    const updateAccessibility = () => {
      const open = isOpen();
      mobileToggle?.setAttribute('aria-expanded', String(open));
      desktopLogo?.setAttribute('aria-expanded', String(open));
      if (mobileQuery.matches) {
        panel.setAttribute('aria-hidden', String(!open));
      } else {
        panel.removeAttribute('aria-hidden');
      }
    };

    const setState = (state) => {
      window.clearTimeout(closeTimer);
      header.dataset.state = state;
      updateAccessibility();
    };

    const focusableSelector = [
      'a[href]',
      'button:not([disabled])',
      'summary',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');

    const visibleFocusables = () => Array.from(header.querySelectorAll(focusableSelector))
      .filter((element) => element !== mobileToggle && element.getClientRects().length > 0 && !element.closest('[aria-hidden="true"]'));

    const setBackgroundInert = (inert, force = false) => {
      if (!mobileQuery.matches && !force) inert = false;
      if (inert) {
        if (inertedElements.length) return;
        inertedElements = Array.from(document.body.children).filter((element) => !element.contains(header));
        inertedElements.forEach((element) => { element.inert = true; });
      } else {
        inertedElements.forEach((element) => { element.inert = false; });
        inertedElements = [];
      }
    };

    const setView = (view) => {
      const cartOpen = view === 'cart';
      header.dataset.view = view;
      navigationView?.setAttribute('aria-hidden', String(cartOpen));
      cartView?.setAttribute('aria-hidden', String(!cartOpen));
    };

    const setMobileDialogState = (open) => {
      if (mobileQuery.matches && open) {
        panel.setAttribute('role', 'dialog');
        panel.setAttribute('aria-modal', 'true');
        mobileToggle?.setAttribute('tabindex', '-1');
        setBackgroundInert(true);
      } else {
        panel.removeAttribute('role');
        panel.removeAttribute('aria-modal');
        mobileToggle?.removeAttribute('tabindex');
        setBackgroundInert(false);
      }
    };

    const open = (trigger) => {
      if (trigger) lastTrigger = trigger;
      setState('open');
      if (mobileQuery.matches) {
        document.documentElement.classList.add('polomi-lux-scroll-locked');
        setMobileDialogState(true);
        window.requestAnimationFrame(() => {
          const firstPanelControl = panel.querySelector(focusableSelector);
          (firstPanelControl || panel).focus();
        });
      }
    };

    const showNavigationView = ({ focusBag = false } = {}) => {
      setView('navigation');
      document.documentElement.classList.toggle('polomi-lux-scroll-locked', mobileQuery.matches && isOpen());
      setMobileDialogState(isOpen());
      if (focusBag) {
        window.requestAnimationFrame(() => header.querySelector('[data-cart-drawer-toggle]')?.focus());
      }
    };

    const showCartView = () => {
      if (!header.contains(document.activeElement)) lastTrigger = document.activeElement;
      setView('cart');
      setState('open');
      document.documentElement.classList.add('polomi-lux-scroll-locked');
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-modal', 'true');
      panel.setAttribute('aria-hidden', 'false');
      mobileToggle?.setAttribute('tabindex', '-1');
      setBackgroundInert(true, true);
      window.requestAnimationFrame(() => {
        (cartView?.querySelector('[data-polomi-cart-back]') || cartView?.querySelector(focusableSelector) || panel).focus();
      });
    };

    const adoptCartDrawer = () => {
      const cartDrawer = document.querySelector('[data-cart-drawer]');
      if (!cartDrawer || !cartSlot || cartSlot.contains(cartDrawer)) return;
      cartSlot.appendChild(cartDrawer);
      cartDrawer.inert = false;
      inertedElements = inertedElements.filter((element) => element !== cartDrawer);
    };

    const close = ({ restoreFocus = false } = {}) => {
      if (header.dataset.view === 'cart' && window.Alpine?.store('modals')?.rightDrawer?.open) {
        window.Alpine.store('modals').close('cart');
      }
      document.documentElement.classList.remove('polomi-lux-scroll-locked');
      setMobileDialogState(false);
      setView('navigation');
      setState('closing');
      const duration = reduceMotion.matches ? 0 : 520;
      closeTimer = window.setTimeout(() => setState('closed'), duration);
      if (restoreFocus && lastTrigger) lastTrigger.focus();
    };

    const scheduleDesktopClose = () => {
      if (mobileQuery.matches || header.dataset.view === 'cart') return;
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(() => {
        if (!header.matches(':focus-within') && !panel.matches(':hover') && !desktopLogo.matches(':hover') && !hoverZone?.matches(':hover')) close();
      }, 180);
    };

    hoverZone?.addEventListener('mouseenter', () => open());
    hoverZone?.addEventListener('mouseleave', scheduleDesktopClose);
    desktopLogo?.addEventListener('mouseenter', () => open(desktopLogo));
    desktopLogo?.addEventListener('focus', () => open(desktopLogo));
    desktopLogo?.addEventListener('mouseleave', scheduleDesktopClose);
    panel?.addEventListener('mouseenter', () => {
      if (!mobileQuery.matches) open();
    });
    panel?.addEventListener('mouseleave', scheduleDesktopClose);
    header.addEventListener('focusout', (event) => {
      if (!header.contains(event.relatedTarget)) scheduleDesktopClose();
    });

    mobileToggle?.addEventListener('click', () => {
      if (isOpen()) close({ restoreFocus: true });
      else open(mobileToggle);
    });

    cartToggle?.addEventListener('click', (event) => {
      event.preventDefault();
      registerCartModal()?.open('cart');
    });

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-polomi-cart-trigger]');
      if (!trigger || header.contains(trigger)) return;

      const modals = registerCartModal();
      if (!modals) return;

      event.preventDefault();
      lastTrigger = trigger;
      modals.open('cart');
    });

    closeButtons.forEach((button) => button.addEventListener('click', () => close({ restoreFocus: true })));

    panel.addEventListener('click', (event) => {
      const backButton = event.target.closest('[data-polomi-cart-back]');
      if (backButton) {
        window.Alpine?.store('modals')?.close('cart');
        showNavigationView({ focusBag: true });
        return;
      }
      if (
        mobileQuery.matches
        && event.target.closest('a')
        && !event.target.closest('[data-cart-drawer-toggle]')
        && !event.target.closest('[data-polomi-cart-view]')
      ) close();
    });

    window.addEventListener('cart-will-open', showCartView);
    window.addEventListener('cart-will-close', () => showNavigationView());
    window.addEventListener('polomi:cart:close-panel', () => close({ restoreFocus: true }));

    const cartObserver = new MutationObserver(adoptCartDrawer);
    cartObserver.observe(document.body, { childList: true, subtree: true });
    adoptCartDrawer();

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && isOpen()) close({ restoreFocus: mobileQuery.matches });
      if (event.key === 'Tab' && mobileQuery.matches && isOpen()) {
        const focusables = visibleFocusables();
        if (!focusables.length) {
          event.preventDefault();
          panel.focus();
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (event.shiftKey && (document.activeElement === first || !header.contains(document.activeElement))) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    });

    mobileQuery.addEventListener('change', () => {
      document.documentElement.classList.remove('polomi-lux-scroll-locked');
      setMobileDialogState(false);
      setState('closed');
      setView('navigation');
    });

    setView('navigation');
    setState('closed');

    updateAccessibility();
  }

  const initialiseAll = (root = document) => root.querySelectorAll(SELECTOR).forEach(initialiseHeader);

  const setCartCount = (count) => {
    if (!Number.isFinite(count)) return;
    document.querySelectorAll('[data-polomi-cart-count]').forEach((element) => {
      element.textContent = String(count);
    });
  };

  const countFromEvent = (event) => {
    const countHTML = event.detail?.response?.sections?.['cart-item-count'];
    if (!countHTML) return null;
    const parsed = new DOMParser().parseFromString(countHTML, 'text/html');
    const count = Number.parseInt(parsed.body.textContent.trim(), 10);
    return Number.isFinite(count) ? count : null;
  };

  const refreshCartCount = async (event) => {
    const eventCount = event ? countFromEvent(event) : null;
    if (eventCount !== null) {
      setCartCount(eventCount);
      return;
    }
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      setCartCount(cart.item_count);
    } catch (_) {
      // Keep the server-rendered value if cart state cannot be refreshed.
    }
  };

  initialiseAll();
  document.addEventListener('shopify:section:load', (event) => initialiseAll(event.target));
  ['baseline:cart:afteradditem', 'baseline:cart:cartqtychange', 'baseline:cart:update'].forEach((eventName) => {
    window.addEventListener(eventName, refreshCartCount);
  });
  window.addEventListener('pageshow', () => refreshCartCount());
})();
